/**
 * Blog Snippet Editor modal.
 *
 * Used by:
 *   - "+ New snippet" in the blog editor sidebar
 *   - Edit on any row of the manage drawer or Settings → Blog Snippets page
 *
 * Renders a split-pane dialog:
 *   Left: name / description / scope / position / auto-apply / AI instruction / HTML body
 *   Right: live landing-page preview inside an iframe using the same LANDING_CSS
 *          as the full blog landing preview so the writer sees real typography.
 */
import { client } from "../api/client.js";
import { escapeHtml } from "./escapeHtml.js";
import { toast } from "./toast.js";
import { LANDING_CSS } from "./landingBlogPreview.js";

const POSITIONS = [
    { key: 'end', label: 'End of post', hint: 'Appended after the model\'s last paragraph' },
    { key: 'start', label: 'Start of post', hint: 'Prepended above the hook paragraph' },
    { key: 'before_cta', label: 'Before CTA', hint: 'Inserted just above the closing CTA paragraph' },
    { key: 'replace_cta', label: 'Replace CTA', hint: 'Removes the model\'s CTA and uses this instead' },
    { key: 'after_first_h2', label: 'After first H2', hint: 'Inserted right after the first section heading' },
];

export function openBlogSnippetEditor({ snippet = null, onSaved = null } = {}) {
    closeBlogSnippetEditor();

    const isEdit = !!snippet;
    const initial = snippet || {
        name: '',
        description: '',
        content_html: '<p>Write your reusable HTML here. Headings, lists, links, and images all work.</p>',
        ai_instruction: '',
        position: 'end',
        auto_apply: true,
        scope: 'brand',
    };
    if (initial.scope == null) initial.scope = initial.brand_id ? 'brand' : 'global';

    const overlay = document.createElement('div');
    overlay.id = 'blog-snippet-editor-overlay';
    overlay.className = 'blog-snippet-editor-overlay';
    overlay.innerHTML = `
        <div class="blog-snippet-editor-shell" role="dialog" aria-label="Blog snippet editor">
            <div class="blog-snippet-editor-topbar">
                <div>
                    <div class="blog-snippet-editor-title">${isEdit ? 'Edit snippet' : 'New blog snippet'}</div>
                    <div class="blog-snippet-editor-sub">Reusable HTML block injected into every generated blog post at the chosen position.</div>
                </div>
                <div class="blog-snippet-editor-actions">
                    <button class="btn btn-ghost btn-sm" id="blog-snippet-cancel">Cancel</button>
                    <button class="btn btn-primary btn-sm" id="blog-snippet-save">${isEdit ? 'Save changes' : 'Create snippet'}</button>
                </div>
            </div>
            <div class="blog-snippet-editor-body">
                <div class="blog-snippet-editor-form">
                    <label class="bs-label">Name</label>
                    <input type="text" id="bs-name" class="bs-input" placeholder="FAQ block, Footer disclaimer..." value="${escapeHtml(initial.name)}" />

                    <label class="bs-label">Description (optional)</label>
                    <input type="text" id="bs-description" class="bs-input" placeholder="One-line explanation shown in the snippet list" value="${escapeHtml(initial.description || '')}" />

                    <label class="bs-label">Scope</label>
                    <div class="bs-radio-row">
                        <label class="bs-radio">
                            <input type="radio" name="bs-scope" value="brand" ${initial.scope === 'brand' ? 'checked' : ''}>
                            <span>🎨 This brand only</span>
                        </label>
                        <label class="bs-radio">
                            <input type="radio" name="bs-scope" value="global" ${initial.scope === 'global' ? 'checked' : ''}>
                            <span>🌐 Global (all brands)</span>
                        </label>
                    </div>

                    <label class="bs-label">Position</label>
                    <select id="bs-position" class="bs-input">
                        ${POSITIONS.map((p) => `<option value="${p.key}" ${initial.position === p.key ? 'selected' : ''}>${escapeHtml(p.label)} — ${escapeHtml(p.hint)}</option>`).join('')}
                    </select>

                    <label class="bs-checkbox-row">
                        <input type="checkbox" id="bs-auto-apply" ${initial.auto_apply ? 'checked' : ''}>
                        <span>Auto-apply to every new blog post</span>
                    </label>
                    <div class="bs-hint">If off, the snippet stays dormant until you check it in the editor sidebar.</div>

                    <label class="bs-label">AI instruction (optional)</label>
                    <textarea id="bs-ai-instruction" class="bs-textarea" rows="2" placeholder="e.g. Do not write your own FAQ section — one is being appended separately.">${escapeHtml(initial.ai_instruction || '')}</textarea>
                    <div class="bs-hint">Added to the AI's system prompt whenever this snippet is active so the model doesn't duplicate or conflict with it.</div>

                    <label class="bs-label">HTML body</label>
                    <div class="bs-html-editor-wrap">
                        <div class="bs-html-editor-tabs">
                            <button class="bs-html-tab active" data-html-view="visual">Visual</button>
                            <button class="bs-html-tab" data-html-view="code">HTML</button>
                        </div>
                        <div class="bs-html-editor-panes">
                            <div id="bs-html-visual" class="bs-html-visual blog-content-editor" contenteditable="true">${initial.content_html || ''}</div>
                            <textarea id="bs-html-code" class="bs-html-code" style="display:none" spellcheck="false">${escapeHtml(initial.content_html || '')}</textarea>
                        </div>
                    </div>
                </div>
                <div class="blog-snippet-editor-preview">
                    <div class="bs-preview-label">Live landing preview</div>
                    <iframe id="bs-preview-frame" title="Snippet preview"></iframe>
                </div>
            </div>
        </div>
    `;
    document.body.appendChild(overlay);

    const frame = overlay.querySelector('#bs-preview-frame');
    const visualEl = overlay.querySelector('#bs-html-visual');
    const codeEl = overlay.querySelector('#bs-html-code');

    function currentHtml() {
        // Whichever pane is currently visible is the source of truth.
        return codeEl.style.display === 'none' ? visualEl.innerHTML : codeEl.value;
    }

    function renderPreview() {
        const html = currentHtml();
        const doc = `<!DOCTYPE html><html><head><meta charset="UTF-8"><link href="https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=DM+Sans:ital,opsz,wght@0,9..40,300..800;1,9..40,300..800&display=swap" rel="stylesheet"><style>${LANDING_CSS}body{padding:24px 16px}</style></head><body><div class="blog-post" style="padding:0;max-width:100%"><div class="blog-post-body">${html}</div></div></body></html>`;
        frame.srcdoc = doc;
    }
    renderPreview();

    // Live preview debounce
    let previewTimer = null;
    function schedulePreview() {
        clearTimeout(previewTimer);
        previewTimer = setTimeout(renderPreview, 250);
    }
    visualEl.addEventListener('input', schedulePreview);
    codeEl.addEventListener('input', schedulePreview);

    // Visual/HTML pane toggle
    overlay.querySelectorAll('.bs-html-tab').forEach((tab) => {
        tab.addEventListener('click', () => {
            overlay.querySelectorAll('.bs-html-tab').forEach((t) => t.classList.remove('active'));
            tab.classList.add('active');
            const view = tab.dataset.htmlView;
            if (view === 'visual') {
                visualEl.innerHTML = codeEl.value;
                visualEl.style.display = '';
                codeEl.style.display = 'none';
            } else {
                codeEl.value = visualEl.innerHTML;
                visualEl.style.display = 'none';
                codeEl.style.display = '';
            }
            renderPreview();
        });
    });

    // Close handlers
    overlay.addEventListener('click', (e) => { if (e.target === overlay) closeBlogSnippetEditor(); });
    overlay.querySelector('#blog-snippet-cancel').addEventListener('click', closeBlogSnippetEditor);
    const escHandler = (e) => { if (e.key === 'Escape') { closeBlogSnippetEditor(); document.removeEventListener('keydown', escHandler); } };
    document.addEventListener('keydown', escHandler);

    // Save
    overlay.querySelector('#blog-snippet-save').addEventListener('click', async () => {
        const name = overlay.querySelector('#bs-name').value.trim();
        if (!name) { toast('warn', 'Snippet name is required'); return; }
        const body = {
            name,
            description: overlay.querySelector('#bs-description').value.trim(),
            position: overlay.querySelector('#bs-position').value,
            auto_apply: overlay.querySelector('#bs-auto-apply').checked,
            ai_instruction: overlay.querySelector('#bs-ai-instruction').value.trim(),
            content_html: currentHtml(),
            scope: overlay.querySelector('input[name="bs-scope"]:checked')?.value || 'brand',
        };
        try {
            const res = isEdit
                ? await client.updateBlogSnippet(snippet.id, body)
                : await client.createBlogSnippet(body);
            if (res?.ok) {
                toast('success', isEdit ? 'Snippet saved' : 'Snippet created');
                closeBlogSnippetEditor();
                onSaved && onSaved(res.snippet);
            } else {
                toast('error', res?.error || 'Save failed');
            }
        } catch (err) {
            toast('error', err.message || 'Save failed');
        }
    });
}

export function closeBlogSnippetEditor() {
    const existing = document.getElementById('blog-snippet-editor-overlay');
    if (existing) existing.remove();
}
